/**
 * Removes everything Superelectric Ink keeps on this device, after an explicit local removal request: the writing, its history, keys, the license
 * certificate, settings and caches. Backups and folders the user chose (the
 * Markdown mirror, a backup folder) are their own copies and are left alone.
 *
 * Every step runs even if an earlier one fails, so as little as possible is
 * left behind; the first error is rethrown at the end.
 */
import { LocalYjsDocument } from '../pages/yjsLocalPersistence'
import { discardLocalSaveRetries, suspendLocalSaving, resumeLocalSaving, drainLocalWrites } from '../pages/localSaveState'
import { discardMemoryPageDrafts } from '../pages/pageDraftJournal'
import { db } from '../platform/webStorage'
import { keyManager } from '../crypto/keyManager'
import { getPlatformBridge } from '../platform/platformBridge'
import { clearLocalEntitlement } from './entitlementService'

export async function wipeLocalData(): Promise<void> {
  await LocalYjsDocument.closeEveryDocument()
  suspendLocalSaving()
  await drainLocalWrites()
  discardLocalSaveRetries()
  discardMemoryPageDrafts()
  let firstError: unknown = null
  const attempt = async (step: () => Promise<unknown> | unknown) => {
    try {
      await step()
    } catch (error) {
      console.error('[LocalWipe] A step failed:', error)
      firstError ??= error
    }
  }

  await attempt(() => keyManager.clear())
  await attempt(() => keyManager.clearLocalCache())
  await attempt(() => clearLocalEntitlement())
  await attempt(() => getPlatformBridge()?.deleteLocalCorpus?.())
  // Dexie's delete() closes the connection first, so open handles don't block it.
  await attempt(() => db.delete())
  await attempt(() => localStorage.clear())
  await attempt(() => sessionStorage.clear())

  // Release the native corpus owner only after every writing/cache store has
  // been removed. A partial wipe must never expose leftover sync work to a new account.
  if (!firstError) await attempt(() => getPlatformBridge()?.restoreLocalAccountId?.(null))
  if (firstError) { resumeLocalSaving(); throw firstError }
}
